/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

/**
 * Product card second image.
 *
 * Desktop is handled purely in CSS (opacity cross-fade on hover). On touch the
 * two images become a native scroll-snap slider; this element only flags the
 * first card the visitor sees so that CSS can play a short nudge animation
 * hinting that the image can be swiped. Registered as a custom element so that
 * cards rendered later (facet filtering, quick view, recommendations) pick up
 * the behaviour automatically.
 */
(function () {
  const HINT_LIMIT = 1; // Only the first card the visitor sees is nudged.
  const HINT_DELAY = 1000; // How long the card must sit fully on screen first.
  const HINT_DURATION = 1000; // Keep in sync with the CSS animation.

  let hintsPlayed = 0;

  const isTouchLayout = () => !window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  class HoverMedia extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".product-item__media-track");

      if (!this.track) {
        return;
      }

      this.dots = Array.from(this.querySelectorAll(".product-item__media-dot"));
      this._onScroll = this._onScroll.bind(this);
      this._onUserInteraction = this._onUserInteraction.bind(this);

      this.track.addEventListener("scroll", this._onScroll, { passive: true });
      this.track.addEventListener("touchstart", this._onUserInteraction, { passive: true });
      this.track.addEventListener("pointerdown", this._onUserInteraction, { passive: true });

      this._observeForHint();
    }

    disconnectedCallback() {
      if (!this.track) {
        return;
      }

      this.track.removeEventListener("scroll", this._onScroll);
      this.track.removeEventListener("touchstart", this._onUserInteraction);
      this.track.removeEventListener("pointerdown", this._onUserInteraction);

      this._stopHint();
    }

    _onScroll() {
      if (this.scrollFrame || this.dots.length === 0) {
        return;
      }

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;

        const slideWidth = this.track.clientWidth;

        if (slideWidth === 0) {
          return;
        }

        const activeIndex = Math.round(this.track.scrollLeft / slideWidth);
        this.dots.forEach((dot, index) => dot.classList.toggle("is-active", index === activeIndex));
      });
    }

    /**
     * The hint belongs to the first card of the grid. Relying on whichever card
     * happens to become visible first is racy — it can land on any card of the
     * first rows depending on scroll restoration and layout shifts.
     */
    _isFirstInGrid() {
      const card = this.closest("product-item");
      const grid = card && (card.closest(".product-list__inner") || card.closest("product-list"));

      return Boolean(grid) && grid.querySelector("product-item") === card;
    }

    _observeForHint() {
      if (!isTouchLayout() || prefersReducedMotion() || hintsPlayed >= HINT_LIMIT) {
        return;
      }

      if (!this._isFirstInGrid()) {
        return;
      }

      const card = this.closest("product-item") || this;

      // Watch the whole card, not just the image: a card whose top third is on
      // screen already shows most of the image, and nudging it there goes
      // unnoticed. A card taller than the viewport can never be fully visible,
      // so it settles for "most of it".
      const requiredRatio = card.getBoundingClientRect().height <= window.innerHeight ? 0.98 : 0.75;

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];

          // Restart the countdown on every change: the nudge only plays once the
          // card has been sitting fully on screen for HINT_DELAY.
          clearTimeout(this.hintTimer);

          if (entry.isIntersecting && entry.intersectionRatio >= requiredRatio) {
            this.hintTimer = setTimeout(() => this._playHint(), HINT_DELAY);
          }
        },
        { threshold: [requiredRatio] }
      );

      this.observer.observe(card);
    }

    _playHint() {
      if (this.userInteracted || hintsPlayed >= HINT_LIMIT) {
        return;
      }

      hintsPlayed += 1;
      this._stopHint();
      this.classList.add("is-hinting");
      this.hintTimer = setTimeout(() => this.classList.remove("is-hinting"), HINT_DURATION);
    }

    _stopHint() {
      clearTimeout(this.hintTimer);

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    _onUserInteraction() {
      this.userInteracted = true;
      this._stopHint();
      this.classList.remove("is-hinting");
    }
  }

  if (!window.customElements.get("hover-media")) {
    window.customElements.define("hover-media", HoverMedia);
  }
})();
